import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { JoinGameMessage } from '../../../shared/types';

interface PitchData {
  player1: { pitch: number; timestamp: number } | null;
  player2: { pitch: number; timestamp: number } | null;
}

interface UseWebSocketReturn {
  socket: Socket | null;
  connected: boolean;
  reconnecting: boolean;
  pitchData: PitchData;
  connectedPlayers: { player1: boolean; player2: boolean };
  error: string | null;
  gamePaused: boolean;
  pausedWaitingFor: 1 | 2 | null;
  joinGame: (gameId: string) => void;
}

export function useWebSocket(serverUrl: string): UseWebSocketReturn {
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [pitchData, setPitchData] = useState<PitchData>({
    player1: null,
    player2: null
  });
  const [error, setError] = useState<string | null>(null);
  const [gamePaused, setGamePaused] = useState(false);
  const [pausedWaitingFor, setPausedWaitingFor] = useState<1 | 2 | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second
  
  // Track which players have joined (separate from pitch data)
  const [connectedPlayers, setConnectedPlayers] = useState<{
    player1: boolean;
    player2: boolean;
  }>({ player1: false, player2: false });

  const clearReconnectTimeout = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const attemptReconnect = () => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnection attempts reached for display');
      setError('Connection failed after multiple attempts');
      setReconnecting(false);
      return;
    }

    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
      30000 // Max 30 seconds
    );

    console.log(`Display attempting reconnection ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts} in ${delay}ms`);
    setReconnecting(true);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }, delay);
  };

  useEffect(() => {
    // Initialize socket connection
    console.log('Attempting to connect to:', serverUrl);
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'], // Allow fallback to polling
      timeout:10000, // 10 second timeout
      forceNew: true, // Force new connection
      autoConnect: true
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      setConnected(true);
      setReconnecting(false);
      setError(null);
      reconnectAttemptsRef.current = 0;
      clearReconnectTimeout();
    });

    socket.on('disconnect', (reason) => {
      console.log('Disconnected from WebSocket server:', reason);
      setConnected(false);

      // Only attempt reconnection for certain disconnect reasons
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
        setError('Disconnected by server');
      } else {
        // Network issues, attempt reconnection
        attemptReconnect();
      }
    });

    socket.on('connect_error', (err: any) => {
      console.error('Display connection error:', err);
      setConnected(false);
      setError('Failed to connect to server');
      attemptReconnect();
    });

    socket.on('error', (err: any) => {
      console.error('WebSocket error:', err);
      setError(err.message || 'Connection error');
    });

    // Handle server messages
    socket.on('message', (message: any) => {
      switch (message.type) {
        case 'playerAssignment':
          // Track when players join
          if (message.success && message.playerId) {
            console.log(`Player ${message.playerId} connected`);
            setConnectedPlayers(prev => ({
              ...prev,
              [`player${message.playerId}`]: true
            }));
          }
          break;
        case 'playerConnected':
          // Handle when other players connect (sent to display only)
          if (message.success && message.playerId) {
            console.log(`Player ${message.playerId} connected`);
            setConnectedPlayers(prev => ({
              ...prev,
              [`player${message.playerId}`]: true
            }));
          }
          break;
        case 'countdown':
          console.log('Countdown:', message.seconds);
          // Countdown messages will be handled by the game state hook
          break;
        case 'gameStart':
          console.log('Game started!');
          // Game start messages will be handled by the game state hook
          break;
        case 'pitch':
          // Handle pitch data from controllers (no logging - too noisy)
          // Note: Server sends messages with 'player' field (converted from deviceId)
          setPitchData(prev => ({
            ...prev,
            [`player${message.player}`]: {
              pitch: message.pitch,
              timestamp: message.timestamp
            }
          }));
          break;
        case 'playerDisconnected':
          console.log(`Player ${message.playerId} disconnected`);
          // Update connectedPlayers when a player disconnects
          setConnectedPlayers(prev => ({
            ...prev,
            [`player${message.playerId}`]: false
          }));
          break;
        case 'gamePaused':
          console.log(`Game paused, waiting for Player ${message.waitingForPlayer}`);
          setGamePaused(true);
          setPausedWaitingFor(message.waitingForPlayer);
          break;
        case 'gameResumed':
          console.log('Game resumed');
          setGamePaused(false);
          setPausedWaitingFor(null);
          break;
        case 'playerReconnected':
          console.log(`Player ${message.playerId} reconnected`);
          setConnectedPlayers(prev => ({
            ...prev,
            [`player${message.playerId}`]: true
          }));
          break;
        case 'error':
          console.error('Server error:', message);
          setError(message.message || 'Server error');
          break;
        default:
          console.log('Unknown message type:', message.type);
      }
    });

    return () => {
      clearReconnectTimeout();
      socket.disconnect();
    };
  }, [serverUrl]);

  const deviceIdRef = useRef<string | null>(null);

  const joinGame = (gameId: string) => {
    if (socketRef.current && connected) {
      // Generate device ID once and reuse it
      if (!deviceIdRef.current) {
        deviceIdRef.current = generateDeviceId();
      }
      
      const message: JoinGameMessage = {
        type: 'joinGame',
        gameId,
        deviceId: deviceIdRef.current,
        clientType: 'display'
      };
      
      console.log('Joining game:', message);
      socketRef.current.emit('message', message);
    }
  };

  return {
    socket: socketRef.current,
    connected,
    reconnecting,
    pitchData,
    connectedPlayers,
    error,
    gamePaused,
    pausedWaitingFor,
    joinGame
  };
}

function generateDeviceId(): string {
  // Generate a simple device ID for this session
  return 'display-' + Math.random().toString(36).substring(2, 11);
}